import type { FieldSpec } from "../templates/types";
import type { NormalizeResult } from "./normalize";
import type { FieldStatus, FieldValue } from "./types";

export type ValidationOutcome = {
  status: Extract<FieldStatus, "filled" | "needs_confirmation">;
  value: FieldValue;
};

/**
 * Per-type validation rules (§8). Two things this function must never do:
 *
 * 1. Silently clamp an out-of-range integer into range. An answer outside
 *    the declared min/max becomes `needs_confirmation` so the engine asks
 *    once — clamping would quietly substitute a value nobody said.
 * 2. Coerce a `verbatim` value. It is never validated in the typed sense;
 *    it is either present (accepted) or not.
 *
 * Takes a NormalizeResult (not a raw value) so the "unmappable" case —
 * normalize.ts returning `null` — is handled in exactly one place: it
 * always becomes `needs_confirmation`, never a silent default.
 */
export function validate(normalized: NormalizeResult, field: FieldSpec): ValidationOutcome {
  if (normalized === null) {
    return { status: "needs_confirmation", value: null };
  }

  const { value } = normalized;

  switch (field.type) {
    case "integer": {
      const n = value as number;
      const min = field.min ?? Number.NEGATIVE_INFINITY;
      const max = field.max ?? Number.POSITIVE_INFINITY;
      if (n < min || n > max) {
        return { status: "needs_confirmation", value };
      }
      return { status: "filled", value };
    }

    case "enum": {
      const options = field.options ?? [];
      if (options.length > 0 && !options.includes(value as string)) {
        return { status: "needs_confirmation", value };
      }
      return { status: "filled", value };
    }

    case "boolean_with_notes":
      return { status: "filled", value };

    case "string_array": {
      const items = value as string[];
      return items.length > 0
        ? { status: "filled", value }
        : { status: "needs_confirmation", value };
    }

    case "verbatim":
      // Never coerced. merge.ts appends verbatim entries directly and never
      // routes them through normalize/validate in the first place; this
      // branch exists only so the switch stays exhaustive.
      return { status: "filled", value };

    default: {
      const exhaustive: never = field.type;
      throw new Error(`validate: unhandled field type ${String(exhaustive)}`);
    }
  }
}
